import UserCard from "components/card/UserCard";
import Menu from "components/common/Menu";
import SearchBar from "components/common/SearchBar";
import UsersHeader from "components/header/UsersHeader";
import AppCopy from "copy/AppCopy";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CommonStyles } from "styles/CommonStyles";
import { useTabActive } from "hook/useTabActive";
import { SideBarTabs } from "constants/tabs";
import { UserType } from "types/loadedData";
import useUser from "hook/useUser";
import PageHeader from "components/common/PageHeader";
import EmptyState from "components/common/EmptyState";

const USERS_PAGE_LIMIT = 20;
const SEEN_USERS_STORAGE_KEY = "bosscar-dashboard-seen-user-ids";

const getStoredSeenUserIds = (): Set<string> => {
  try {
    const stored = localStorage.getItem(SEEN_USERS_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch (error) {
    return new Set();
  }
};

const persistSeenUserIds = (seenIds: Set<string>) => {
  try {
    localStorage.setItem(
      SEEN_USERS_STORAGE_KEY,
      JSON.stringify(Array.from(seenIds).slice(-5000))
    );
  } catch (error) {}
};

const mergeUniqueUsers = (current: UserType[], next: UserType[]) => {
  const seen = new Set(current.map((user) => String(user._id)));
  const uniqueNext = next.filter((user) => !seen.has(String(user._id)));
  return [...current, ...uniqueNext];
};

function UsersPage() {
  const { getUsers } = useUser();
  const [searchValue, setSearchValue] = useState<string>("");
  const [activeSearch, setActiveSearch] = useState<string>("");

  const [loadedUsers, setLoadedUsers] = useState<UserType[]>([]);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [count, setCount] = useState(0);
  const [seenUserIds, setSeenUserIds] = useState<Set<string>>(
    getStoredSeenUserIds
  );
  const listRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const getUsersRef = useRef(getUsers);
  const fetchingRef = useRef(false);
  const didInitialLoadRef = useRef(false);

  useTabActive(SideBarTabs.Users.name);

  useEffect(() => {
    getUsersRef.current = getUsers;
  }, [getUsers]);

  const loadUsers = useCallback(
    async ({
      append = false,
      offset = 0,
      search = activeSearch,
    }: {
      append?: boolean;
      offset?: number;
      search?: string;
    } = {}) => {
      if (fetchingRef.current) return;
      fetchingRef.current = true;
      const searchQuery = search.trim();

      try {
        const response = await getUsersRef.current(
          {
            setLoading: setLoadingMore,
            limit: USERS_PAGE_LIMIT,
            offset,
            search: searchQuery || undefined,
          },
          {
            local: true,
            callBack: (amount) => setCount(amount),
          }
        );

        if (response) {
          setLoadedUsers((current) =>
            append ? mergeUniqueUsers(current, response) : response
          );
        }
      } finally {
        fetchingRef.current = false;
      }
    },
    [activeSearch]
  );

  useEffect(() => {
    if (didInitialLoadRef.current) return;
    didInitialLoadRef.current = true;
    loadUsers();
  }, [loadUsers]);

  const handleOnSearchValueChange = (e: any) => {
    setSearchValue(e.target.value);
  };

  const handleSearchKeydown = (e: any) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleSearch = async () => {
    const nextSearch = searchValue.trim();
    setActiveSearch(nextSearch);
    await loadUsers({ append: false, offset: 0, search: nextSearch });
  };

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || loadedUsers.length >= count) return;
    await loadUsers({ append: true, offset: loadedUsers.length });
  }, [count, loadedUsers.length, loadUsers, loadingMore]);

  const markUserSeen = useCallback((id: string) => {
    setSeenUserIds((current) => {
      if (current.has(id)) return current;
      const next = new Set(current);
      next.add(id);
      persistSeenUserIds(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || loadedUsers.length >= count) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: listRef.current,
        rootMargin: "180px",
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [count, handleLoadMore, loadedUsers.length]);

  const { searchPlaceholder, titleText } = AppCopy.usersPage;

  return (
    <div className={`${CommonStyles.pageWrapper} `}>
      <Menu />
      <PageHeader
        title={titleText}
        description={count ? `${count} registered users` : "Manage registered users."}
      />
      <div className={ClassName.container}>
        <SearchBar
          onKeyDown={handleSearchKeydown}
          onSearch={handleSearch}
          value={searchValue}
          onChange={handleOnSearchValueChange}
          placeholder={searchPlaceholder}
        />
        <section className={ClassName.section}>
          <UsersHeader />
          <div ref={listRef} className={ClassName.list}>
            {loadedUsers.map((user, index) => (
              <UserCard
                key={String(user._id || index)}
                user={user}
                isNew={!seenUserIds.has(String(user._id))}
                onSeen={markUserSeen}
              />
            ))}
            {!loadingMore && !loadedUsers.length ? (
              <EmptyState
                title="No users found"
                description="Try a different name, email, or phone number."
              />
            ) : null}
            {loadedUsers.length < count && (
              <div ref={sentinelRef} className={ClassName.loadMoreSentinel}>
                {loadingMore ? "Loading more users..." : ""}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default UsersPage;

const ClassName = {
  container: "flex flex-col gap-3 text-primaryText",
  section:
    "flex min-h-0 flex-1 flex-col rounded-2xl border border-slate-200 bg-white p-4",
  list: "max-h-[calc(100vh-260px)] min-h-56 overflow-y-auto",
  loadMoreSentinel:
    "flex h-14 w-full items-center justify-center text-sm text-secondaryText",
};
